import { FC } from 'react';
import { VStack, Text, Box, Image, SimpleGrid, HStack } from '@chakra-ui/react';
import {
  Droppable,
  Draggable,
  DragDropContext,
  DropResult,
} from 'react-beautiful-dnd';

import './FormFields.css';
import { FormField } from '.';
import { fieldTypeMapper, fieldIconMapper } from '../utils';
import { ITEMS } from '../utils';
import { useAppDispatch } from '../../../hooks';
import AlertCircleIcon from '../../../assets/forms/alert-circle.svg';
import { copy, reorder } from '../form-slice';
import { FormFiled } from '../../../models';

export const FormFields: FC<{ fields: FormFiled[] }> = ({ fields }) => {
  const dispatch = useAppDispatch();

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }

    switch (source.droppableId) {
      case 'LIST':
        dispatch(
          reorder({ startIndex: source.index, endIndex: destination.index })
        );
        break;
      case 'ITEMS':
        dispatch(
          copy({ droppableSource: source, droppableDestination: destination })
        );

        break;
    }
  };

  return (
    <Box mx="2">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="ITEMS" isDropDisabled={true}>
          {(provided, snapshot) => (
            <SimpleGrid
              columns={6}
              spacing={2}
              mt="4"
              ref={provided.innerRef}
              boxShadow="0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08);"
              style={{
                border: snapshot.isDraggingOver
                  ? '1px dashed #000'
                  : '1px solid transparent',
              }}
              p="4"
              borderRadius="md"
            >
              {ITEMS.map((item, index) => (
                <Draggable
                  key={item.id}
                  draggableId={String(item.id)}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <>
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={provided.draggableProps.style}
                        border="1px dashed"
                        borderColor={
                          snapshot.isDragging ? 'black' : 'transparent'
                        }
                      >
                        <HStack
                          userSelect="none"
                          borderRadius="md"
                          border="1px"
                          borderColor="custom_gray.4"
                          box-shadow="0px 1px 2px 0px rgba(16, 24, 40, 0.05)"
                          px="2"
                          py="2"
                          w="full"
                          draggable
                        >
                          <Image src={fieldIconMapper(item.type)} />
                          <Text fontSize="sm">
                            {fieldTypeMapper(item.type)}
                          </Text>
                        </HStack>
                      </Box>
                      {snapshot.isDragging && (
                        <HStack
                          className="clone"
                          userSelect="none"
                          borderRadius="md"
                          border="1px"
                          borderColor="custom_gray.4"
                          box-shadow="0px 1px 2px 0px rgba(16, 24, 40, 0.05)"
                          px="8"
                          py="2"
                          w="full"
                          draggable
                        >
                          <Image src={fieldIconMapper(item.type)} />
                          <Text fontSize="sm">{item.type}</Text>
                        </HStack>
                      )}
                    </>
                  )}
                </Draggable>
              ))}
            </SimpleGrid>
          )}
        </Droppable>

        <Droppable droppableId="LIST">
          {(provided, snapshot) => (
            <VStack
              border="2px"
              borderRadius="lg"
              borderStyle="dashed"
              borderColor="custom_orange.2"
              py="3"
              mt="4"
              ref={provided.innerRef}
              // style={{
              //   border: snapshot.isDraggingOver
              //     ? '1px dashed #000'
              //     : '1px solid #ddd',
              // }}
            >
              {fields.length
                ? fields.map((item, index) => (
                    <Draggable
                      key={item.id}
                      draggableId={String(item.id)}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            width: '100%',
                          }}
                        >
                          <FormField field={item} provided={provided} />
                        </div>
                      )}
                    </Draggable>
                  ))
                : provided.placeholder && (
                    <>
                      <Box
                        border="1px"
                        borderColor="custom_gray.4"
                        borderRadius="md"
                        p="2"
                      >
                        <Image src={AlertCircleIcon} />
                      </Box>

                      <Text color="custom_blue.1" fontSize="sm">
                        <Box
                          as="span"
                          color="custom_orange.2"
                          fontWeight="semibold"
                        >
                          Drag and drop
                        </Box>{' '}
                        your field type.
                      </Text>
                    </>
                  )}
              {provided.placeholder}
            </VStack>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};
